import pluralize from "pluralize";

import * as icons from "@/shared/icons.js";

// Repository type to icon name mapping
const REPO_ICONS = {
  Bitbucket: "bitbucket",
  GitHub: "github",
  GitLab: "gitlab",
};

// Contact type to icon type mapping
const CONTACT_ICONS = {
  email: "mail",
  slack: "slack",
  slack_handle: "slack",
  web: "global",
  microsoft_teams: "microsoft_teams",
};

export const repositoryIcon = (type) => {
  return REPO_ICONS[type];
};

export const contactIcon = (type) => {
  return CONTACT_ICONS[type];
};

const EXPLICIT_ICON_MAPPING = {
  googlecloudplatform: "GoogleCloud",
  googlecloud: "GoogleCloud",
  google: "GoogleCloud",
  gcp: "GoogleCloud",
  azure: "AzureResources",
  microsoft: "AzureDevops",
  msazure: "AzureDevops",
  azureresources: "AzureResources",
  aws: "AwsAmazonCom",
  amazon: "AwsAmazonCom",
  amazonwebservice: "AwsAmazonCom",
  amazonwebservices: "AwsAmazonCom",
};

// Every whole word of the input, plus every run of consecutive words joined together,
// so "google cloud logs" offers "google", "googlecloud", "googlecloudlogs", "cloud"...
// Each run is offered in its singular form too, so "api docs" can reach ApidocIcon.
const getWordRuns = (inputName) => {
  const words = inputName
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter(Boolean);
  const runs = new Set();

  words.forEach((_word, start) => {
    let run = "";

    words.slice(start).forEach((nextWord) => {
      run += nextWord;
      runs.add(run);
      runs.add(pluralize.singular(run));
    });
  });

  return runs;
};

export const getIconViaFuzzyMatching = (iconInputName) => {
  if (!iconInputName) {
    return null;
  }

  // lowercase matching
  const squashedIconPrefix = iconInputName.replace(/[\s.\-_]/g, "");
  const casedIconName = `${squashedIconPrefix
    .charAt(0)
    .toUpperCase()}${squashedIconPrefix.slice(1).toLowerCase()}Icon`;
  const squashedIcon = icons[casedIconName];

  if (squashedIcon) {
    return squashedIcon;
  }

  // PascalCase matching
  const pascalIconPrefix = iconInputName
    .replace(/[\s.\-_]+/g, " ")
    .toLowerCase()
    .split(" ")
    .reduce((acc, cur) => {
      return acc + cur.charAt(0).toUpperCase() + cur.slice(1);
    }, "");
  const pascalIcon = icons[pascalIconPrefix + "Icon"];

  if (pascalIcon) {
    return pascalIcon;
  }

  // Explicit allow-list matching
  const lowerCasedPrefix = squashedIconPrefix.toLowerCase();

  if (EXPLICIT_ICON_MAPPING[lowerCasedPrefix]) {
    return icons[EXPLICIT_ICON_MAPPING[lowerCasedPrefix] + "Icon"];
  }

  // Word matching. An icon name has to line up with whole words of the input: matching
  // anywhere inside a word lets an incidental substring claim an icon ("Deployment"
  // contains "deploy", "Escalation" contains "scala") and shadow the url the caller
  // passed alongside the name.
  const wordRuns = getWordRuns(iconInputName);
  const inputContainsIconMatch = Object.keys(icons).find((iconKey) =>
    wordRuns.has(iconKey.toLowerCase().replace("icon", "")),
  );
  const inputContainsIconMatchIcon = icons[inputContainsIconMatch];

  if (inputContainsIconMatchIcon) {
    return inputContainsIconMatchIcon;
  }

  return null;
};

const getNormalizedIconBaseName = (inputName, defaultName = "custom") => {
  if (!inputName) {
    return defaultName;
  }

  const normalizedInput = inputName.toLowerCase().replace(/[\s\-_.]/g, "");
  const iconBaseNames = Object.keys(icons)
    .map((key) => key.replace(/(icon|svg)$/i, "").toLowerCase())
    .filter((name) => !!name)
    .sort((a, b) => b.length - a.length); // sort from longest to shortest

  const found = iconBaseNames.find((base) => normalizedInput.includes(base));

  return found || defaultName;
};

export const getDynamicIntegrationIconType = ({ type, name }) => {
  if (type === "custom" && name) {
    return getNormalizedIconBaseName(name);
  }

  return type || "custom";
};
